import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { usePersistedState } from '../../hooks/usePersistedState'
import BarChart from '../../components/Charts/BarChart'
import DonutChart from '../../components/Charts/DonutChart'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, initializeSliderValues, getSliderIndicesFromValues, TMB_SLIDER_KEY, extractActualMonthValues, checkHasDataForRange } from '../../utils/tmbUtils'
import { exportToPNG, exportToSVG, exportToCSV } from '../../utils/tmbExportUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import '../../styles/shared/index.css'
import './PopulationCharacteristics.css'

const useResponsiveChartHeight = () => {
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 1920,
        height: typeof window !== 'undefined' ? window.innerHeight : 1080
    })

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            })
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const getBarChartHeight = useCallback(() => {
        if (windowSize.width <= 1280 && windowSize.height <= 720) {
            return 250
        } else if (windowSize.width <= 1366 && windowSize.height <= 800) {
            return 290
        }
        return 330
    }, [windowSize])

    const getPieChartHeight = useCallback(() => {
        if (windowSize.width <= 1280 && windowSize.height <= 720) {
            return 165
        } else if (windowSize.width <= 1366 && windowSize.height <= 800) {
            return 180
        }
        return 200
    }, [windowSize])

    const getHorizontalBarHeight = useCallback((numCategories: number) => {
        if (windowSize.width <= 1280 && windowSize.height <= 720) {
            return numCategories * 40
        } else if (windowSize.width <= 1366 && windowSize.height <= 800) {
            return numCategories * 46
        }
        return numCategories * 52
    }, [windowSize])

    return { getBarChartHeight, getPieChartHeight, getHorizontalBarHeight, windowSize }
}

const COLORS = {
    race: '#5F769F',
    age: '#D6887E',
    insurance: '#99B9B6',
    education: '#F2CD62',
    parity: {
        'Multipara': '#06A8BD',
        'Primipara': '#B0B8C5',
        'Not Reported': '#F2CD62'
    },
    deliveryType: {
        'Cesarean section': '#FFBC78',
        'C-section': '#FFBC78',
        'Vaginal': '#4E79A6',
        'Not Reported': '#B0B8C5'
    },
    induced: '#FFBC78',
    complications: '#A6ACAC'
}

const CATEGORY_ORDER: Record<string, string[]> = {
    race: [
        'Hispanic',
        'Black/African American',
        'Black',
        'NH White',
        'White',
        'Non-Hispanic White',
        'Asian',
        'Other',
        'Not reported'
    ],
    age: [
        '10-18',
        '19-24',
        '18-24',
        '25-29',
        '30-34',
        '35-39',
        '40+',
        'Not reported'
    ],
    insurance: [
        'Private',
        'Public',
        'Self-Pay',
        'Uninsured',
        'Not reported'
    ],
    education: [
        'Some elementary school',
        'Some high school',
        'High school graduate',
        'Some college',
        'College degree',
        'Some postgraduate',
        'Postgraduate degree',
        'Not reported'
    ],
    parity: [
        'Not Reported',
        'Nullipara',
        'Multipara',
        'Primipara',
        'Prefer not to answer'
    ],
    deliveryType: [
        'Not Reported',
        'Cesarean section',
        'C-section',
        'Vaginal',
        'Assisted vaginal delivery'
    ],
    induced: [
        'Yes for a medical reason',
        'Yes elective induction',
        'prefer not to answer',
        'Yes elective Induction'
    ],
    complications: [
        'No Complications',
        'Complications'
    ]
}

const sortCategories = (categories: string[], categoryType: string): string[] => {
    const order = CATEGORY_ORDER[categoryType] || []

    const normalize = (str: string): string => {
        if (!str) return ''
        return str.toString().toLowerCase().trim()
            .replace(/\s+/g, ' ')
            .replace(/[^\w\s-]/g, '')
    }

    return [...categories].sort((a, b) => {
        const normalizedA = normalize(a)
        const normalizedB = normalize(b)

        let indexA = order.findIndex(item => normalize(item) === normalizedA)
        let indexB = order.findIndex(item => normalize(item) === normalizedB)

        if (indexA === -1) {
            indexA = order.findIndex(item => {
                const normalizedItem = normalize(item)
                return normalizedA.includes(normalizedItem) || normalizedItem.includes(normalizedA)
            })
        }
        if (indexB === -1) {
            indexB = order.findIndex(item => {
                const normalizedItem = normalize(item)
                return normalizedB.includes(normalizedItem) || normalizedItem.includes(normalizedB)
            })
        }

        if (indexA !== -1 && indexB !== -1) return indexA - indexB
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        return a.localeCompare(b)
    })
}

const mapLabels = (categories: string[], categoryType: string): string[] => {
    return categories.map(label => {
        const normalized = label.toLowerCase().trim()

        if (categoryType === 'race') {
            if (normalized === 'race not documented' || normalized === 'not reported' || normalized === 'unknown') return 'Not reported'
            if (normalized.includes('prefer not to answer') || normalized.includes('prefer not to say')) return 'Not reported'
            if (normalized === 'not reported...') return 'Not reported'
            if (normalized === 'white' || normalized === 'non-hispanic white' || normalized === 'non hispanic white' || normalized === 'nh white') return 'NH White'
            if (normalized === 'black/african american' || normalized === 'black') return 'Black'
        }

        if (categoryType === 'age') {
            if (normalized === 'not reported' || normalized === 'unknown') return 'Not reported'
            if (normalized.includes('prefer not to answer') || normalized.includes('prefer not to say')) return 'Not reported'
            if (label === '18-24') return '19-24'
        }

        if (categoryType === 'insurance') {
            if (normalized.includes('self-pay') || normalized.includes('self pay')) return 'Self-Pay'
            if (normalized.includes('prefer not to answer') || normalized.includes('prefer not to say')) return 'Not reported'
            if (
                normalized === 'not reported' ||
                normalized === 'unknown' ||
                normalized === '' ||
                normalized === 'n/a' ||
                normalized.includes('not documented')
            ) return 'Not reported'
        }

        if (categoryType === 'education') {
            if (
                normalized.includes('not reported') ||
                normalized.includes('unknown') ||
                normalized.includes('prefer not') ||
                normalized === '' ||
                normalized === 'n/a'
            ) {
                return 'Not reported'
            }

            if (
                normalized.includes('elementary') ||
                normalized.includes('grammar school') ||
                normalized.includes('grade school')
            ) {
                return 'Some elementary school'
            }

            if (
                normalized.includes('some high school') ||
                normalized.includes('some hs')
            ) {
                return 'Some high school'
            }

            if (
                normalized.includes('high school graduate') ||
                normalized.includes('high school grad') ||
                normalized.includes('hs graduate') ||
                normalized.includes('hs grad') ||
                normalized.includes('ged') ||
                normalized.includes('high school')
            ) {
                return 'High school graduate'
            }

            if (
                normalized.includes('some college') ||
                normalized.includes('associate')
            ) {
                return 'Some college'
            }

            if (
                normalized.includes('college degree') ||
                normalized.includes('bachelor') ||
                (normalized.includes('college') && normalized.includes('degree'))
            ) {
                return 'College degree'
            }

            if (
                normalized.includes('some postgraduate') ||
                normalized.includes('some post-graduate') ||
                normalized.includes('some graduate school')
            ) {
                return 'Some postgraduate'
            }

            if (
                normalized.includes('postgraduate degree') ||
                normalized.includes('post-graduate degree') ||
                normalized.includes('postgraduate') ||
                normalized.includes('post-graduate') ||
                normalized.includes('master') ||
                normalized.includes('doctorate') ||
                normalized.includes('phd') ||
                normalized.includes('doctor')
            ) {
                return 'Postgraduate degree'
            }
        }

        return label
    })
}

const aggregateValues = (arr: any[]): number => {
    if (!arr || arr.length === 0) return 0

    if (typeof arr[0] === 'object' && arr[0] !== null && 'n' in arr[0] && 'd' in arr[0]) {
        let totalNum = 0
        let totalDenom = 0
        arr.forEach(item => {
            totalNum += (item.n || 0)
            totalDenom += (item.d || 0)
        })
        return totalDenom > 0 ? (totalNum / totalDenom) * 100 : 0
    }

    const validValues = arr.filter(v => typeof v === 'number' && !isNaN(v))
    if (validValues.length === 0) return 0

    return validValues.reduce((sum, val) => sum + val, 0)
}

const calcPcts = (values: number[]): string[] => {
    const total = values.reduce((s, v) => s + v, 0)
    return values.map(v => {
        if (total === 0) return '0%'
        const pct = Math.round((v / total) * 1000) / 10
        return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`
    })
}

const PopulationCharacteristics: React.FC = () => {
    const { selectedHospitalIds } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()

    const { getBarChartHeight, getHorizontalBarHeight } = useResponsiveChartHeight()

    const [monthList, setMonthList] = useState<MonthInfo[]>([])
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)
    const hasDataForRange = checkHasDataForRange(sliderRange[0], sliderRange[1], actualMonths)

    const startIdx = sliderStartIdx
    const endIdx = sliderEndIdx

    const parseDate = (dateStr: string): Date => {
        const val = parseMonthToValue(dateStr)
        if (val === 0) return new Date()

        const year = Math.floor(val / 100)
        const month = val % 100
        return new Date(year, month - 1, 1)
    }

    const generateMonthList = (demographicRows: any[], clinicalRows: any[]): MonthInfo[] => {
        const allRows = [...demographicRows, ...clinicalRows]
        if (allRows.length === 0) return []

        const uniqueDates = new Set<string>()
        let minVal = Infinity
        let maxVal = -Infinity
        let latestDateStr = ''

        allRows.forEach(row => {
            const dateStr = row[2]
            if (dateStr) {
                uniqueDates.add(dateStr)
                const val = parseMonthToValue(dateStr)
                if (val > 0) {
                    if (val < minVal) minVal = val
                    if (val > maxVal) {
                        maxVal = val
                        latestDateStr = dateStr
                    }
                }
            }
        })

        if (!latestDateStr) return []

        const minYear = Math.floor(minVal / 100)
        const minMonth = minVal % 100
        const maxYear = Math.floor(maxVal / 100)
        const maxMonth = maxVal % 100

        const diffYears = maxYear - minYear
        const diffMonths = maxMonth - minMonth
        const totalMonths = Math.max(1, (diffYears * 12) + diffMonths + 1)

        const list: MonthInfo[] = []

        const date = parseDate(latestDateStr)

        for (let i = 0; i < totalMonths; i++) {
            const year = date.getFullYear()
            const month = date.getMonth() + 1
            const label = date.toLocaleString('default', {
                month: 'short',
                year: 'numeric'
            })
            const value = year * 100 + month

            list.unshift({
                label,
                value,
                date: new Date(date)
            })

            date.setMonth(date.getMonth() - 1)
        }

        return list
    }

    const {
        data: demographicsData,
        loading: demographicsLoading,
        initialLoading: demographicsInitialLoading,
        error: demographicsError
    } = useHospitalData(
        '/api/tmb/patient_demographics',
        selectedHospitalIds
    )

    const {
        data: clinicalData,
        loading: clinicalLoading,
        initialLoading: clinicalInitialLoading,
        error: clinicalError
    } = useHospitalData(
        '/api/tmb/clinical_characteristics',
        selectedHospitalIds
    )
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const raceContainerRef = useRef<HTMLDivElement>(null)
    const ageContainerRef = useRef<HTMLDivElement>(null)
    const insuranceContainerRef = useRef<HTMLDivElement>(null)
    const educationContainerRef = useRef<HTMLDivElement>(null)
    const parityContainerRef = useRef<HTMLDivElement>(null)
    const deliveryTypeContainerRef = useRef<HTMLDivElement>(null)
    const inducedContainerRef = useRef<HTMLDivElement>(null)
    const complicationsContainerRef = useRef<HTMLDivElement>(null)
    const combinedRightPanelRef = useRef<HTMLDivElement>(null)

    const demographicExportOptions = { cssContextClass: 'population-page', svgExpandLeft: 90, hideSelectors: ['.chart-title-wrapper'] }

    const handleDownloadPNG = async (ref: React.RefObject<HTMLDivElement>, sectionTitle: string, chartTitle: string, filename: string) => {
        if (!ref.current) return
        await exportToPNG(ref.current, filename, { title: sectionTitle, subtitle: chartTitle }, demographicExportOptions)
    }

    const handleDownloadSVG = async (ref: React.RefObject<HTMLDivElement>, sectionTitle: string, chartTitle: string, filename: string) => {
        if (!ref.current) return
        await exportToSVG(ref.current, filename, { title: sectionTitle, subtitle: chartTitle }, demographicExportOptions)
    }

    const clinicalExportOptions = { cssContextClass: 'population-page', hideSelectors: ['.chart-title-wrapper'] }

    const handleDownloadClinicalPNG = async (ref: React.RefObject<HTMLDivElement>, chartTitle: string, filename: string) => {
        if (!ref.current) return
        await exportToPNG(ref.current, filename, { title: 'Clinical Characteristics', subtitle: chartTitle }, clinicalExportOptions)
    }

    const handleDownloadClinicalSVG = async (ref: React.RefObject<HTMLDivElement>, chartTitle: string, filename: string) => {
        if (!ref.current) return
        await exportToSVG(ref.current, filename, { title: 'Clinical Characteristics', subtitle: chartTitle }, clinicalExportOptions)
    }

    const handleDownloadCSV = (labels: string[], values: number[], _title: string, filename: string, percentages?: string[]) => {
        const headers = percentages ? ['Category', 'Count', 'Percentage'] : ['Category', 'Value']
        const rows = labels.map((label, i) =>
            percentages ? [label, values[i], percentages[i]] : [label, values[i]]
        )
        exportToCSV({ headers, rows, filename })
    }


    const closeMenu = (menuId: string) => {
        const menu = document.getElementById(menuId)
        if (menu) {
            menu.classList.remove('show')
        }
    }

    const toggleMenu = (menuId: string) => {
        document.querySelectorAll('.export-dropdown.show').forEach(el => {
            if (el.id !== menuId) el.classList.remove('show')
        })
        const menu = document.getElementById(menuId)
        if (menu) {
            menu.classList.toggle('show')
        }
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.export-menu')) {
                document.querySelectorAll('.export-dropdown.show').forEach(el => {
                    el.classList.remove('show')
                })
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    useEffect(() => {
        if ((demographicsData || clinicalData) && monthList.length === 0) {
            const demoRows = demographicsData?.response?.result?.rows || []
            const clinicalRows = clinicalData?.response?.result?.rows || []

            const demoMonths = extractActualMonthValues(demoRows, 2)
            const clinicalMonths = extractActualMonthValues(clinicalRows, 2)
            const combined = new Set([...demoMonths, ...clinicalMonths])
            setActualMonths(combined)
            const months = generateMonthList(demoRows, clinicalRows)

            if (months.length > 0) {
                setMonthList(months)

                const newRange = initializeSliderValues(months, sliderRange, 12)
                if (newRange[0] !== sliderRange[0] || newRange[1] !== sliderRange[1]) {
                    setSliderRange(newRange)
                }
            }
        }
    }, [demographicsData, clinicalData, monthList.length])

        useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
        }, []);
    const getIsDateInWindow = (dateStr: string): boolean => {
        const minVal = sliderRange[0] || 0
        const maxVal = sliderRange[1] || 0
        if (!minVal || !maxVal) return true

        const rowVal = parseMonthToValue(dateStr)
        if (rowVal === 0) return false

        return rowVal >= minVal && rowVal <= maxVal
    }

    const processedDemographics = useMemo(() => {
        if (!demographicsData) return { data: null }

        const rows = demographicsData?.response?.result?.rows || []
        const categoryIdx = 0
        const surveyMonthIdx = 2
        const subcategoryIdx = 3
        const countIdx = 4

        const data: Record<string, Record<string, number[]>> = {
            race: {},
            age: {},
            insurance: {},
            education: {}
        }

        const notReported: Record<string, number> = { race: 0, age: 0, insurance: 0, education: 0 }

        const isNotReportedSubcat = (s: string): boolean => {
            const k = s.toLowerCase().trim()
            return k === '' || k === 'unknown' || k === 'n/a' ||
                k.includes('not reported') || k.includes('not documented') ||
                k.includes('prefer not')
        }

        rows.forEach((row: any[]) => {
            if (!getIsDateInWindow(row[surveyMonthIdx])) return

            const category = row[categoryIdx]?.toString().toLowerCase().trim()
            const subcategory = row[subcategoryIdx]?.toString().trim()
            const count = parseFloat(row[countIdx]) || 0

            if (!category || !subcategory) return

            let bucket: string | null = null
            if (category.includes('race') || category.includes('ethnicity')) bucket = 'race'
            else if (category.includes('age')) bucket = 'age'
            else if (category.includes('insurance') || category.includes('payor')) bucket = 'insurance'
            else if (category.includes('education')) bucket = 'education'
            if (!bucket) return

            if (isNotReportedSubcat(subcategory)) {
                notReported[bucket] += count
            }

            if (!data[bucket][subcategory]) data[bucket][subcategory] = []
            data[bucket][subcategory].push(count)
        })

        const insuranceKeys = Object.keys(data.insurance).map(k => k.toLowerCase())
        if (!insuranceKeys.some(k => k.includes('private'))) data.insurance['Private'] = [0]
        if (!insuranceKeys.some(k => k.includes('public'))) data.insurance['Public'] = [0]
        if (!insuranceKeys.some(k => k.includes('self'))) data.insurance['Self-Pay'] = [0]
        if (!insuranceKeys.some(k => k.includes('uninsured'))) data.insurance['Uninsured'] = [0]
        if (!insuranceKeys.some(k => k.includes('not reported'))) data.insurance['Not reported'] = [0]

        return { data, notReported }
    }, [demographicsData, startIdx, endIdx, monthList])

    const parityData = useMemo(() => {
        if (!clinicalData) return { labels: [], values: [], colors: [], counts: [], notReportedTotal: 0 }

        const rows = clinicalData.response?.result?.rows || []
        const map: Record<string, number[]> = {}
        let notReportedTotal = 0
        const seenKeys = new Set<string>()

        rows.forEach((row: any[]) => {
            const characteristicType = row[0]?.toString().toLowerCase() || ''
            const hospitalId = row[1]?.toString().trim()
            const surveyMonth = row[2]
            const category = row[3]?.toString().trim()

            if (!getIsDateInWindow(surveyMonth)) return

            if (!characteristicType.includes('parity')) return
            if (!category) return

            const uniqueKey = `${characteristicType}|${hospitalId}|${surveyMonth}|${category}`
            if (seenKeys.has(uniqueKey)) return
            seenKeys.add(uniqueKey)

            const normalizedLabel = category.toLowerCase().trim()
            if (normalizedLabel.includes('prefer not') || normalizedLabel === 'unknown' || normalizedLabel.includes('not reported')) {
                notReportedTotal += Number(row[4]) || 0
                return
            }
            let mappedCategory = category
            if (normalizedLabel.includes('multipara')) {
                mappedCategory = 'Multipara'
            } else if (normalizedLabel.includes('primipara')) {
                mappedCategory = 'Primipara'
            } else if (normalizedLabel.includes('nullipara')) {
                mappedCategory = 'Nullipara'
            }

            const val = Number(row[4]) || 0
            if (!map[mappedCategory]) map[mappedCategory] = []
            map[mappedCategory].push(val)
        })

        const labels = Object.keys(map)
        if (labels.length === 0) return { labels: [], values: [], colors: [], counts: [], notReportedTotal }

        const sorted = sortCategories(labels, 'parity')
        const rawCounts = sorted.map(l => aggregateValues(map[l]))

        const zipped = sorted.map((label, i) => ({ label, count: rawCounts[i] }))
            .filter(item => item.count > 0)

        const filteredLabels = zipped.map(x => x.label)
        const filteredCounts = zipped.map(x => x.count)

        const total = filteredCounts.reduce((s, v) => s + v, 0)
        const MIN_VISUAL_PCT = 3
        const visualValues = total > 0
            ? filteredCounts.map(c => {
                const pct = (c / total) * 100
                return pct > 0 && pct < MIN_VISUAL_PCT ? (MIN_VISUAL_PCT / 100) * total : c
            })
            : filteredCounts

        const colors = filteredLabels.map(l => {
            const normalizedLabel = l.toLowerCase()
            if (normalizedLabel.includes('multipara')) return COLORS.parity['Multipara']
            if (normalizedLabel.includes('primipara')) return COLORS.parity['Primipara']
            if (normalizedLabel.includes('not reported')) return COLORS.parity['Not Reported']
            return '#B0B8C5'
        })

        return { labels: filteredLabels, values: visualValues, counts: filteredCounts, colors, notReportedTotal }
    }, [clinicalData, startIdx, endIdx, monthList])

    const deliveryTypeData = useMemo(() => {
        if (!clinicalData) return { labels: [], values: [], colors: [], counts: [], notReportedTotal: 0 }

        const rows = clinicalData.response?.result?.rows || []
        const map: Record<string, { n: number; d: number }[]> = {}
        let notReportedTotal = 0
        const seenKeys = new Set<string>()

        rows.forEach((row: any[]) => {
            const characteristicType = row[0]?.toString().toLowerCase() || ''
            const hospitalId = row[1]?.toString().trim()
            const surveyMonth = row[2]

            if (!getIsDateInWindow(surveyMonth)) return

            if (!characteristicType.includes('delivery') && !characteristicType.includes('birth')) return
            let category = row[3]

            const numerator = Number(row[4]) || 0

            const normalizedLabel = (category ?? '').toString().toLowerCase().trim()
            if (!category || normalizedLabel.includes('prefer not') || normalizedLabel === 'not reported' || normalizedLabel === 'unknown') {
                notReportedTotal += numerator
                return
            }

            const uniqueKey = `${characteristicType}|${hospitalId}|${surveyMonth}|${category}`
            if (seenKeys.has(uniqueKey)) return
            seenKeys.add(uniqueKey)

            const denominator = Number(row[5]) || 0

            if (!map[category]) map[category] = []
            map[category].push({ n: numerator, d: denominator })
        })

        const labels = Object.keys(map)
        if (labels.length === 0) return { labels: [], values: [], colors: [], counts: [], notReportedTotal }

        const sorted = sortCategories(labels, 'deliveryType')

        const rawPcts = sorted.map(l => aggregateValues(map[l]))
        const totalP = rawPcts.reduce((sum, v) => sum + v, 0)
        const values = totalP > 0
            ? rawPcts.map(p => (p / totalP) * 100)
            : rawPcts.map(() => 0)

        const counts = sorted.map(l =>
            map[l].reduce((sum, item) => sum + (item.n || 0), 0)
        )

        const zipped = sorted.map((label, i) => ({ label, value: values[i], count: counts[i] }))
            .filter(item => item.value > 0)

        const filteredLabels = zipped.map(x => x.label)
        const filteredCounts = zipped.map(x => x.count)

        const MIN_VISUAL_PCT = 5
        const visualValues = zipped.map(x => {
            if (x.value > 0 && x.value < MIN_VISUAL_PCT) return MIN_VISUAL_PCT
            return x.value
        })

        const colors = filteredLabels.map(l => {
            const x = l.toLowerCase()
            if (x.includes('cesarean') || x.includes('c-section')) return COLORS.deliveryType['Cesarean section']
            if (x.includes('vaginal')) return COLORS.deliveryType['Vaginal']
            if (x.includes('not reported')) return COLORS.deliveryType['Not Reported'] || '#B0B8C5'
            return '#4E79A6'
        })

        return { labels: filteredLabels, values: visualValues, colors, counts: filteredCounts, notReportedTotal }
    }, [clinicalData, startIdx, endIdx, monthList])

    const mapInducedCategory = (raw: string): string => {
        const k = raw.toLowerCase().trim().replace(/\s+/g, ' ')
        if (k.includes('not reported') || k.includes('prefer not') || k === 'unknown') return 'Not reported'
        if (k.includes("don't know") || k === "don't know") return "Don't know"
        if (k === 'no' || k.startsWith('no ')) return 'No'
        if (k.includes('medical') && k.includes('reason')) return 'Yes'
        if (k.includes('elective') && k.includes('yes')) return 'Yes'
        if (k.includes('yes')) return 'Yes'
        return raw
    }

    const mapComplicationsCategory = (raw: string): string => {
        const k = raw.toLowerCase().trim().replace(/\s+/g, ' ')
        if (k.includes('not reported') || k.includes('prefer not') || k === 'unknown') return 'Not reported'
        if (k.includes('no') && k.includes('complication')) return 'No Complications'
        if (k.includes('complication')) return 'Complications'
        return raw
    }

    const makeHorizontal = (section: string) => {
        if (!clinicalData) return { categories: [], values: [], counts: [], notReportedTotal: 0 }

        const rows = clinicalData.response?.result?.rows || []
        const map: Record<string, any[]> = {}
        const seenKeys = new Set<string>()

        rows.forEach((row: any[]) => {
            const characteristicType = row[0]?.toString().toLowerCase() || ''
            const hospitalId = row[1]?.toString().trim()
            const surveyMonth = row[2]
            let category = row[3]

            if (!getIsDateInWindow(surveyMonth)) return

            if (!characteristicType.includes(section)) return
            if (!category) return

            const uniqueKey = `${characteristicType}|${hospitalId}|${surveyMonth}|${category}`
            if (seenKeys.has(uniqueKey)) return
            seenKeys.add(uniqueKey)

            if (section === 'induced' || section === 'induction') {
                category = mapInducedCategory(category)
            } else if (section === 'complication') {
                category = mapComplicationsCategory(category)
            }

            const numerator = Number(row[4]) || 0
            const denominator = Number(row[5]) || 0

            if (!map[category]) map[category] = []
            map[category].push({ n: numerator, d: denominator })
        })

        if (section === 'induced' || section === 'induction') {
            ["Don't know", 'Yes', 'No'].forEach(cat => {
                if (!map[cat]) map[cat] = []
            })
        } else if (section === 'complication') {
            ['No Complications', 'Complications'].forEach(cat => {
                if (!map[cat]) map[cat] = []
            })
        }

        const countMap: Record<string, number> = {}
        Object.keys(map).forEach(k => {
            countMap[k] = map[k].reduce((sum, item) => sum + (item?.n || 0), 0)
        })

        const notReportedTotal = countMap['Not reported'] || 0
        delete map['Not reported']
        delete countMap['Not reported']

        const grandTotal = Object.values(countMap).reduce((sum, c) => sum + c, 0)

        const keys = Object.keys(map)
        const rawPcts = keys.map(k => grandTotal > 0 ? (countMap[k] / grandTotal) * 100 : 0)
        const floored = rawPcts.map(p => Math.floor(p * 10) / 10)

        let adjusted = [...floored]
        if (grandTotal > 0) {
            const remainders = rawPcts.map((p, i) => p - floored[i])
            const flooredSum = floored.reduce((s, v) => s + Math.round(v * 10), 0)
            const target = 1000
            let deficit = target - flooredSum

            const indices = keys.map((_, i) => i).sort((a, b) => remainders[b] - remainders[a])
            for (const idx of indices) {
                if (deficit <= 0) break
                adjusted[idx] = Math.round((adjusted[idx] + 0.1) * 10) / 10
                deficit--
            }
        }

        const sorted = keys
            .map((k, i) => ({
                k,
                count: countMap[k],
                v: adjusted[i]
            }))
            .sort((a, b) => b.v - a.v)

        const categories = sorted.map(x => x.k)
        const values = sorted.map(x => x.v)
        const counts = sorted.map(x => x.count)

        return { categories, values, counts, notReportedTotal }
    }

    const inducedData = makeHorizontal('induced')
    const complicationsData = makeHorizontal('complication')

    const prepareBarChartData = (
        data: Record<string, number[]> | undefined,
        categoryType: string,
        sortStrategy: 'order' | 'value' = 'order'
    ) => {
        if (!data || Object.keys(data).length === 0) {
            return { categories: [], values: [] }
        }

        const normalizedData: Record<string, number[]> = {}
        Object.entries(data).forEach(([rawLabel, rawValues]) => {
            const canonicalLabel = mapLabels([rawLabel], categoryType)[0]
            const valuesArray = Array.isArray(rawValues) ? rawValues : [rawValues]

            if (!normalizedData[canonicalLabel]) normalizedData[canonicalLabel] = []
            normalizedData[canonicalLabel].push(...valuesArray)
        })

        if (CATEGORY_ORDER[categoryType]) {
            CATEGORY_ORDER[categoryType].forEach(rawLabel => {
                const canonicalLabel = mapLabels([rawLabel], categoryType)[0]
                if (!normalizedData[canonicalLabel]) {
                    normalizedData[canonicalLabel] = [0]
                }
            })
        }

        const categories = Object.keys(normalizedData)
        if (categories.length === 0) {
            return { categories: [], values: [] }
        }

        let sortedCategories: string[]
        let values: number[]

        if (sortStrategy === 'value') {
            const categoryValues = categories.map(cat => ({
                category: cat,
                value: Math.round(aggregateValues(normalizedData[cat]))
            }))
            categoryValues.sort((a, b) => b.value - a.value)
            sortedCategories = categoryValues.map(item => item.category)
            values = categoryValues.map(item => item.value)
        } else {
            sortedCategories = sortCategories(categories, categoryType)
            values = sortedCategories.map(cat => Math.round(aggregateValues(normalizedData[cat])))
        }

        const mappedCategories = mapLabels(sortedCategories, categoryType)

        const filtered = mappedCategories
            .map((cat, i) => ({ cat, val: values[i] }))
            .filter(x => x.cat.toLowerCase().trim() !== 'not reported')

        return {
            categories: filtered.map(x => x.cat),
            values: filtered.map(x => x.val)
        }
    }

    const error = demographicsError || clinicalError

    const makeDemoTooltip = useCallback((color: string) =>
        ({ series, seriesIndex, dataPointIndex, w }: { series: number[][], seriesIndex: number, dataPointIndex: number, w: any }) => {
            const count = series[seriesIndex]?.[dataPointIndex] ?? 0
            const total = (series[seriesIndex] ?? []).reduce((s: number, v: number) => s + (v || 0), 0)
            const pct = total > 0 ? (count / total) * 100 : 0
            const roundedPct = Math.round(pct * 10) / 10
            const pctStr = Number.isInteger(roundedPct) ? `${roundedPct}%` : `${roundedPct.toFixed(1)}%`
            const category = w.globals.labels[dataPointIndex] || ''
            return `<div style="background:${color};color:#fff;padding:6px 12px;border-radius:4px;font-family:Poppins,Segoe UI,sans-serif;font-size:13px;font-weight:500;">${category}: ${count} (${pctStr})</div>`
        }, [])

    const demoTooltips = useMemo(() => ({
        race:      makeDemoTooltip(COLORS.race),
        age:       makeDemoTooltip(COLORS.age),
        insurance: makeDemoTooltip(COLORS.insurance),
        education: makeDemoTooltip(COLORS.education),
    }), [makeDemoTooltip])

    const raceData = prepareBarChartData(processedDemographics?.data?.race, 'race', 'value')
    const ageData = prepareBarChartData(processedDemographics?.data?.age, 'age', 'order')
    const insuranceData = prepareBarChartData(processedDemographics?.data?.insurance, 'insurance', 'value')
    const educationData = prepareBarChartData(processedDemographics?.data?.education, 'education', 'order')

    const demoNotReported = processedDemographics?.notReported || { race: 0, age: 0, insurance: 0, education: 0 }

    const renderNotReportedFootnote = (count: number) => {
        const n = count && count > 0 ? count : 0
        return (
            <div
                style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    textAlign: 'center',
                    marginTop: 'auto',
                    paddingTop: '6px',
                    fontStyle: 'italic'
                }}
            >
                % of reported patients · Not reported excluded (N={n})
            </div>
        )
    }


    const isInitialLoading = demographicsInitialLoading || clinicalInitialLoading

    if (isInitialLoading) {
        return <LoadingNotice title="Loading population characteristics data..." />
    }

    if (error) {
        return (
            <div className="population-page tmb-page">
                <div className="error-container">
                    <p>Error loading data: {error}</p>
                </div>
            </div>
        )
    }

    return (
        <div id={generatePDFIds.dashboard()} className="population-page tmb-page">
            <div className="content-wrapper">
                <div className="page-header">
                    <h1 className="page-title">Population Characteristics</h1>
                    {globalMonthList.length > 0 && (
                        <div className="date-slider-container">
                            <DateRangeSlider
                                monthList={globalMonthList}
                                startIndex={startIdx}
                                endIndex={endIdx}
                                onChange={(start, end) => setSliderRange([globalMonthList[start]?.value || 0, globalMonthList[end]?.value || 0])}
                            />
                        </div>
                    )}
                </div>

                {!hasDataForRange ? (
                    <div className="section-card" style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                        No data available
                    </div>
                ) : (<>
                <div className="section-card demographics-section-card">
                    <div className="section-header">Patient Demographics</div>

                    <div className="demographics-grid">
                        <div id={generatePDFIds.chart(1)} className="demographic-chart-item" ref={raceContainerRef}>
                            <div className="chart-title-wrapper">
                                <div className="chart-title">Race/Ethnicity</div>
                                <div className="export-menu">
                                    <button className="hamburger" onClick={(e) => { e.stopPropagation(); toggleMenu('export-menu-race'); }}>&#9776;</button>
                                    <div id="export-menu-race" className="export-dropdown">
                                        <div onClick={() => { handleDownloadPNG(raceContainerRef, 'Patient Demographics', 'Race/Ethnicity', 'Race_Ethnicity'); closeMenu('export-menu-race'); }}>Download PNG</div>
                                        <div onClick={() => { handleDownloadSVG(raceContainerRef, 'Patient Demographics', 'Race/Ethnicity', 'Race_Ethnicity'); closeMenu('export-menu-race'); }}>Download SVG</div>
                                        <div onClick={() => { handleDownloadCSV(raceData.categories, raceData.values, 'Race/Ethnicity', 'Race_Ethnicity', calcPcts(raceData.values)); closeMenu('export-menu-race'); }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            {(raceData.categories.length > 0 && raceData.values.some((v: number) => v > 0) || demographicsLoading) ? (
                                <BarChart
                                    key={`race-${JSON.stringify(raceData.values)}`}
                                    categories={raceData.categories}
                                    series={[{ name: 'Count', data: raceData.values }]}
                                    height={getBarChartHeight()}
                                    horizontal={false}
                                    colors={[COLORS.race]}
                                    tooltipCustom={demoTooltips.race}
                                    showToolbar={false}
                                    exportFilename="Race_Ethnicity"
                                    noDataMessage="No race/ethnicity data available"
                                    yAxisShow={false}
                                    xAxisShow={true}
                                    showGrid={false}
                                    dataLabelsEnabled={false}
                                    isLoading={demographicsLoading}
                                    showXAxisBorder={false}
                                    xAxisLabelRotate={-35}
                                    xAxisLabelRotateAlways={true}
                                    xAxisLabelMinHeight={100}
                                    xAxisLabelMaxHeight={100}
                                    xAxisLabelOffsetX={4}
                                    xAxisHideOverlappingLabels={false}
                                    xAxisTrim={false}
                                    plotOptionsBar={{ columnWidth: '55%' }}
                                    gridPadding={{ left: 12, right: 8, top: -15, bottom: 8 }}
                                />
                            ) : (
                                <div className="no-data-message">No race/ethnicity data available</div>
                            )}
                            {renderNotReportedFootnote(demoNotReported.race)}
                        </div>

                        <div id={generatePDFIds.chart(2)} className="demographic-chart-item" ref={ageContainerRef}>
                            <div className="chart-title-wrapper">
                                <div className="chart-title">Age</div>
                                <div className="export-menu">
                                    <button className="hamburger" onClick={(e) => { e.stopPropagation(); toggleMenu('export-menu-age'); }}>&#9776;</button>
                                    <div id="export-menu-age" className="export-dropdown">
                                        <div onClick={() => { handleDownloadPNG(ageContainerRef, 'Patient Demographics', 'Age', 'Age'); closeMenu('export-menu-age'); }}>Download PNG</div>
                                        <div onClick={() => { handleDownloadSVG(ageContainerRef, 'Patient Demographics', 'Age', 'Age'); closeMenu('export-menu-age'); }}>Download SVG</div>
                                        <div onClick={() => { handleDownloadCSV(ageData.categories, ageData.values, 'Age', 'Age', calcPcts(ageData.values)); closeMenu('export-menu-age'); }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            {(ageData.categories.length > 0 && ageData.values.some((v: number) => v > 0) || demographicsLoading) ? (
                                <BarChart
                                    key={`age-${JSON.stringify(ageData.values)}`}
                                    categories={ageData.categories}
                                    series={[{ name: 'Count', data: ageData.values }]}
                                    height={getBarChartHeight()}
                                    horizontal={false}
                                    colors={[COLORS.age]}
                                    tooltipCustom={demoTooltips.age}
                                    showToolbar={false}
                                    exportFilename="Age"
                                    noDataMessage="No age data available"
                                    yAxisShow={false}
                                    xAxisShow={true}
                                    showGrid={false}
                                    dataLabelsEnabled={false}
                                    isLoading={demographicsLoading}
                                    showXAxisBorder={false}
                                    xAxisLabelRotate={-35}
                                    xAxisLabelRotateAlways={true}
                                    xAxisLabelMinHeight={100}
                                    xAxisLabelMaxHeight={100}
                                    xAxisLabelOffsetX={4}
                                    xAxisHideOverlappingLabels={false}
                                    xAxisTrim={false}
                                    plotOptionsBar={{ columnWidth: '55%' }}
                                    gridPadding={{ left: 12, right: 8, top: -15, bottom: 8 }}
                                />
                            ) : (
                                <div className="no-data-message">No age data available</div>
                            )}
                            {renderNotReportedFootnote(demoNotReported.age)}
                        </div>

                        <div id={generatePDFIds.chart(3)} className="demographic-chart-item" ref={insuranceContainerRef}>
                            <div className="chart-title-wrapper">
                                <div className="chart-title">Insurance</div>
                                <div className="export-menu">
                                    <button className="hamburger" onClick={(e) => { e.stopPropagation(); toggleMenu('export-menu-insurance'); }}>&#9776;</button>
                                    <div id="export-menu-insurance" className="export-dropdown">
                                        <div onClick={() => { handleDownloadPNG(insuranceContainerRef, 'Patient Demographics', 'Insurance', 'Insurance'); closeMenu('export-menu-insurance'); }}>Download PNG</div>
                                        <div onClick={() => { handleDownloadSVG(insuranceContainerRef, 'Patient Demographics', 'Insurance', 'Insurance'); closeMenu('export-menu-insurance'); }}>Download SVG</div>
                                        <div onClick={() => { handleDownloadCSV(insuranceData.categories, insuranceData.values, 'Insurance', 'Insurance', calcPcts(insuranceData.values)); closeMenu('export-menu-insurance'); }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            {(insuranceData.categories.length > 0 && insuranceData.values.some((v: number) => v > 0) || demographicsLoading) ? (
                                <BarChart
                                    key={`ins-${JSON.stringify(insuranceData.values)}`}
                                    categories={insuranceData.categories}
                                    series={[{ name: 'Count', data: insuranceData.values }]}
                                    height={getBarChartHeight()}
                                    horizontal={false}
                                    colors={[COLORS.insurance]}
                                    tooltipCustom={demoTooltips.insurance}
                                    showToolbar={false}
                                    exportFilename="Insurance"
                                    noDataMessage="No insurance data available"
                                    yAxisShow={false}
                                    xAxisShow={true}
                                    showGrid={false}
                                    dataLabelsEnabled={false}
                                    isLoading={demographicsLoading}
                                    showXAxisBorder={false}
                                    xAxisLabelRotate={-35}
                                    xAxisLabelRotateAlways={true}
                                    xAxisLabelMinHeight={100}
                                    xAxisLabelMaxHeight={100}
                                    xAxisLabelOffsetX={4}
                                    xAxisHideOverlappingLabels={false}
                                    xAxisTrim={false}
                                    plotOptionsBar={{ columnWidth: '55%' }}
                                    gridPadding={{ left: 12, right: 8, top: -15, bottom: 8 }}
                                />
                            ) : (
                                <div className="no-data-message">No insurance data available</div>
                            )}
                            {renderNotReportedFootnote(demoNotReported.insurance)}
                        </div>

                        <div id={generatePDFIds.chart(4)} className="demographic-chart-item" ref={educationContainerRef}>
                            <div className="chart-title-wrapper">
                                <div className="chart-title">Education</div>
                                <div className="export-menu">
                                    <button className="hamburger" onClick={(e) => { e.stopPropagation(); toggleMenu('export-menu-education'); }}>&#9776;</button>
                                    <div id="export-menu-education" className="export-dropdown">
                                        <div onClick={() => { handleDownloadPNG(educationContainerRef, 'Patient Demographics', 'Education', 'Education'); closeMenu('export-menu-education'); }}>Download PNG</div>
                                        <div onClick={() => { handleDownloadSVG(educationContainerRef, 'Patient Demographics', 'Education', 'Education'); closeMenu('export-menu-education'); }}>Download SVG</div>
                                        <div onClick={() => { handleDownloadCSV(educationData.categories, educationData.values, 'Education', 'Education', calcPcts(educationData.values)); closeMenu('export-menu-education'); }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            {(educationData.categories.length > 0 && educationData.values.some((v: number) => v > 0) || demographicsLoading) ? (
                                <BarChart
                                    key={`edu-${JSON.stringify(educationData.values)}`}
                                    categories={educationData.categories}
                                    series={[{ name: 'Count', data: educationData.values }]}
                                    height={getBarChartHeight()}
                                    horizontal={false}
                                    colors={[COLORS.education]}
                                    tooltipCustom={demoTooltips.education}
                                    showToolbar={false}
                                    exportFilename="Education"
                                    noDataMessage="No education data available"
                                    yAxisShow={false}
                                    xAxisShow={true}
                                    showGrid={false}
                                    dataLabelsEnabled={false}
                                    isLoading={demographicsLoading}
                                    showXAxisBorder={false}
                                    xAxisLabelRotate={-35}
                                    xAxisLabelRotateAlways={true}
                                    xAxisLabelMinHeight={100}
                                    xAxisLabelMaxHeight={100}
                                    xAxisLabelOffsetX={4}
                                    xAxisHideOverlappingLabels={false}
                                    xAxisTrim={false}
                                    plotOptionsBar={{ columnWidth: '55%' }}
                                    gridPadding={{ left: 12, right: 8, top: -15, bottom: 8 }}
                                />
                            ) : (
                                <div className="no-data-message">No education data available</div>
                            )}
                            {renderNotReportedFootnote(demoNotReported.education)}
                        </div>
                    </div>
                </div>

                <div className="section-card clinical-section-card">
                    <div className="section-header">Clinical Characteristics</div>

                    <div className="clinical-charts-row">
                        <div id={generatePDFIds.chart(5)} className="clinical-chart-item" ref={parityContainerRef}>
                            <div className="chart-title-wrapper">
                                <div className="chart-title">Parity</div>
                                <div className="export-menu">
                                    <button className="hamburger" onClick={(e) => { e.stopPropagation(); toggleMenu('export-menu-parity'); }}>&#9776;</button>
                                    <div id="export-menu-parity" className="export-dropdown">
                                        <div onClick={() => { handleDownloadClinicalPNG(parityContainerRef, 'Parity', 'Parity'); closeMenu('export-menu-parity'); }}>Download PNG</div>
                                        <div onClick={() => { handleDownloadClinicalSVG(parityContainerRef, 'Parity', 'Parity'); closeMenu('export-menu-parity'); }}>Download SVG</div>
                                        <div onClick={() => { handleDownloadCSV(parityData.labels, parityData.values, 'Parity', 'Parity'); closeMenu('export-menu-parity'); }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            {((parityData.labels.length > 0 && parityData.values.some(v => v > 0)) || clinicalLoading) ? (
                                <DonutChart
                                    labels={parityData.labels}
                                    series={parityData.values}
                                    colors={parityData.colors}
                                    tooltipCounts={parityData.counts}
                                    type="pie"
                                    showLegend={true}
                                    legendPosition={isMobile ? "bottom" : "left"}
                                    legendFloating={!isMobile}
                                    pieOffsetX={isMobile ? 0 : 18}
                                    pieOffsetY={isMobile ? 0 : 40}
                                    legendHorizontalAlign="left"
                                    legendOffsetX={0}
                                    legendOffsetY={0}
                                    legendFontSize="10px"
                                    legendFontWeight="500"
                                    legendMarkerSize="8"
                                    legendItemMarginHorizontal={4}
                                    legendItemMarginVertical={0}
                                    dataLabelColor="#555"
                                    dataLabelDropShadow={false}
                                    dataLabelOffset={isMobile ? -5 : 30}
                                    height={170}
                                    showInfoIcon={false}
                                    exportFilename="Parity"
                                    noDataMessage="No parity data available"
                                    isLoading={clinicalLoading}
                                    useCustomLegend={false}
                                    minAngleToShowLabel={0}
                                    dataLabelDecimalPrecision={1}
                                    dataLabelFontSize="11px"
                                    dataLabelFontWeight="400"
                                />
                            ) : (
                                <div className="no-data-message">No parity data available</div>
                            )}
                            {renderNotReportedFootnote(parityData.notReportedTotal)}
                        </div>

                        <div id={generatePDFIds.chart(6)} className="clinical-chart-item" ref={deliveryTypeContainerRef}>
                            <div className="chart-title-wrapper">
                                <div className="chart-title">Delivery Type</div>
                                <div className="export-menu">
                                    <button className="hamburger" onClick={(e) => { e.stopPropagation(); toggleMenu('export-menu-delivery'); }}>&#9776;</button>
                                    <div id="export-menu-delivery" className="export-dropdown">
                                        <div onClick={() => { handleDownloadClinicalPNG(deliveryTypeContainerRef, 'Delivery Type', 'Delivery_Type'); closeMenu('export-menu-delivery'); }}>Download PNG</div>
                                        <div onClick={() => { handleDownloadClinicalSVG(deliveryTypeContainerRef, 'Delivery Type', 'Delivery_Type'); closeMenu('export-menu-delivery'); }}>Download SVG</div>
                                        <div onClick={() => { handleDownloadCSV(deliveryTypeData.labels, deliveryTypeData.values, 'Delivery Type', 'Delivery_Type'); closeMenu('export-menu-delivery'); }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            {((deliveryTypeData.labels.length > 0 && deliveryTypeData.values.some(v => v > 0)) || clinicalLoading) ? (
                                <DonutChart
                                    labels={deliveryTypeData.labels}
                                    series={deliveryTypeData.values}
                                    colors={deliveryTypeData.colors}
                                    tooltipCounts={deliveryTypeData.counts}
                                    type="pie"
                                    showLegend={true}
                                    legendPosition={isMobile ? "bottom" : "left"}
                                    legendFloating={!isMobile}
                                    pieOffsetX={isMobile ? 0 : 18}
                                    pieOffsetY={isMobile ? 0 : 40}
                                    legendHorizontalAlign="left"
                                    legendOffsetX={0}
                                    legendOffsetY={0}
                                    legendFontSize="10px"
                                    legendFontWeight="500"
                                    legendMarkerSize="8"
                                    legendItemMarginHorizontal={4}
                                    legendItemMarginVertical={0}
                                    dataLabelColor="#555"
                                    dataLabelDropShadow={false}
                                    dataLabelOffset={isMobile ? -5 : 30}
                                    height={170}
                                    showInfoIcon={false}
                                    exportFilename="Delivery_Type"
                                    noDataMessage="No delivery type data available"
                                    isLoading={clinicalLoading}
                                    useCustomLegend={false}
                                    minAngleToShowLabel={0}
                                    dataLabelDecimalPrecision={1}
                                    dataLabelFontSize="11px"
                                    dataLabelFontWeight="400"
                                />
                            ) : (
                                <div className="no-data-message">No delivery type data available</div>
                            )}
                            {renderNotReportedFootnote(deliveryTypeData.notReportedTotal)}
                        </div>

                        <div id={generatePDFIds.chart(7)} className="clinical-chart-item clinical-right-panel" ref={combinedRightPanelRef}>
                            <div className="horizontal-chart-wrapper" ref={inducedContainerRef}>
                                <div className="chart-title-wrapper">
                                    <div className="chart-title">Induced</div>
                                    <div className="export-menu">
                                        <button className="hamburger" onClick={(e) => { e.stopPropagation(); toggleMenu('export-menu-induced'); }}>&#9776;</button>
                                        <div id="export-menu-induced" className="export-dropdown">
                                            <div onClick={() => { handleDownloadClinicalPNG(inducedContainerRef, 'Induced', 'Induced'); closeMenu('export-menu-induced'); }}>Download PNG</div>
                                            <div onClick={() => { handleDownloadClinicalSVG(inducedContainerRef, 'Induced', 'Induced'); closeMenu('export-menu-induced'); }}>Download SVG</div>
                                            <div onClick={() => { handleDownloadCSV(inducedData.categories, inducedData.values, 'Induced', 'Induced'); closeMenu('export-menu-induced'); }}>Download CSV</div>
                                        </div>
                                    </div>
                                </div>
                                {clinicalLoading ? (
                                        <div className="loading-text">Loading...</div>
                                    ) : inducedData.categories.length > 0 && inducedData.values.some((v: number) => v > 0) ? (
                                        <div className="bar-rows">
                                            {inducedData.categories.map((cat, i) => (
                                                <div
                                                    className={`bar-row${inducedData.values[i] === 0 ? ' bar-row-zero' : ''}`}
                                                    key={cat}
                                                >
                                                    <span
                                                        className="bar-row-tooltip"
                                                        style={{ backgroundColor: COLORS.induced }}
                                                    >
                                                        {cat}: {inducedData.counts[i]} (
                                                        {inducedData.values[i] > 0
                                                            ? inducedData.values[i].toFixed(1)
                                                            : '0.0'}
                                                        %)
                                                    </span>

                                                    <span className="bar-row-label">{cat}</span>

                                                    {inducedData.values[i] > 0 && (
                                                        <div className="bar-row-track">
                                                            <div
                                                                className="bar-row-fill"
                                                                style={{
                                                                    width: `${Math.min(inducedData.values[i], 100)}%`,
                                                                    backgroundColor: COLORS.induced
                                                                }}
                                                            />
                                                        </div>
                                                    )}

                                                    <span className="bar-row-pct">
                                                        {inducedData.values[i] > 0
                                                            ? `${inducedData.values[i].toFixed(1)}%`
                                                            : '0.0%'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-data-message">No induced data available</div>
                                    )}
                                {!clinicalLoading && renderNotReportedFootnote(inducedData.notReportedTotal)}
                            </div>

                            <div className="horizontal-chart-wrapper" ref={complicationsContainerRef}>
                                <div className="chart-title-wrapper">
                                    <div className="chart-title">Complications</div>
                                    <div className="export-menu">
                                        <button className="hamburger" onClick={(e) => { e.stopPropagation(); toggleMenu('export-menu-complications'); }}>&#9776;</button>
                                        <div id="export-menu-complications" className="export-dropdown">
                                            <div onClick={() => { handleDownloadClinicalPNG(complicationsContainerRef, 'Complications', 'Complications'); closeMenu('export-menu-complications'); }}>Download PNG</div>
                                            <div onClick={() => { handleDownloadClinicalSVG(complicationsContainerRef, 'Complications', 'Complications'); closeMenu('export-menu-complications'); }}>Download SVG</div>
                                            <div onClick={() => { handleDownloadCSV(complicationsData.categories, complicationsData.values, 'Complications', 'Complications'); closeMenu('export-menu-complications'); }}>Download CSV</div>
                                        </div>
                                    </div>
                                </div>
                                {clinicalLoading ? (
                                        <div className="loading-text">Loading...</div>
                                    ) : complicationsData.categories.length > 0 && complicationsData.values.some((v: number) => v > 0) ? (
                                        <div className="bar-rows">
                                            {complicationsData.categories.map((cat, i) => (
                                                <div
                                                    className={`bar-row${complicationsData.values[i] === 0 ? ' bar-row-zero' : ''}`}
                                                    key={cat}
                                                >
                                                    <span
                                                        className="bar-row-tooltip"
                                                        style={{ backgroundColor: COLORS.complications }}
                                                    >
                                                        {cat}: {complicationsData.counts[i]} (
                                                        {complicationsData.values[i] > 0
                                                            ? complicationsData.values[i].toFixed(1)
                                                            : '0.0'}
                                                        %)
                                                    </span>

                                                    <span className="bar-row-label">{cat}</span>

                                                    {complicationsData.values[i] > 0 && (
                                                        <div className="bar-row-track">
                                                            <div
                                                                className="bar-row-fill"
                                                                style={{
                                                                    width: `${Math.min(complicationsData.values[i], 100)}%`,
                                                                    backgroundColor: COLORS.complications
                                                                }}
                                                            />
                                                        </div>
                                                    )}

                                                    <span className="bar-row-pct">
                                                        {complicationsData.values[i] > 0
                                                            ? `${complicationsData.values[i].toFixed(1)}%`
                                                            : '0.0%'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-data-message">No complications data available</div>
                                    )}
                                {!clinicalLoading && renderNotReportedFootnote(complicationsData.notReportedTotal)}
                            </div>
                        </div>
                    </div>
                </div>
                </>)}
            </div>
        </div>
    )
}

export default PopulationCharacteristics